import { useEffect, useState, useMemo } from 'react'
import { useApi } from '../../../api/api'
import { useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import { UploadSimpleIcon, FileIcon, TrashIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopDialog,
    ScoopTextField,
    ScoopText,
    ScoopSelector,
    ScoopRadioButton,
    ScoopDropzone,
    ScoopSpinner,
    ScoopColumn,
    ScoopRow,
    useToast,
} from '../../ui'
import { EVENTS, trackEvent } from '../../../events'
import {
    POLLING_CHECK_ACTIONS,
    useProcessingPolling,
} from '../../../api/hooks/processing/useProcessingPolling'
import styles from './FileUpload.module.scss'

// when user uploads an image to S3, make sure the filename is safe
function safeS3FileName(str) {
    return str.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/ /g, '_')
}

export default function FileUpload({ isOpen, onClose, stepBack, goNext, isOnboarding }) {
    const toast = useToast()
    const { acceptedFiles, getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
        useDropzone({
            maxFiles: 1,
            accept: {
                // 'application/vnd.ms-excel': [],
                'text/csv': [],
                'text/xlsx': [],
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                'application/zip': [],
                'application/pdf': [],
                'text/html': [],
            },
            onDrop: (acceptedFiles, rejectedFiles) => {
                if (acceptedFiles.length > 1 || rejectedFiles.length > 1) {
                    toast.error('You can only upload one file at a time.')
                }
            },
        })

    // Hook to fetch data
    const { postData: postDataDefault } = useApi()
    const { postData: postDataWorkspaceactions } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/workspaceactions'
    )
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const userName = useSelector((state) => state.auth.userName)
    // State to store inboxes
    const [inboxes, setInboxes] = useState([])
    const [selectedInbox, setSelectedInbox] = useState('')
    const [newInboxName, setNewInboxName] = useState('')
    const [newInboxRange, setNewInboxRange] = useState('')
    const [newInboxDescription, setNewInboxDescription] = useState('')
    const [loading, setLoading] = useState(false)
    const [creationPolling, setCreationPolling] = useState(false)

    // polling for inbox creation
    useProcessingPolling(
        POLLING_CHECK_ACTIONS.CREATION,
        creationPolling,
        setCreationPolling,
        { inboxName: newInboxName },
        () => {
            goNext(newInboxName)
            setLoading(false)
        },
        150, // Max polling attempts (150 * 2 seconds = 5 minutes)
        () => null,
        workspaceID
    )

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await postDataDefault({
                    action: 'getInboxInformation',
                    workspaceID: workspaceID,
                    userID: userID,
                    userName: userName,
                    isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
                })

                // Set the fetched inbox data
                if (data && data.inboxes && Array.isArray(data.inboxes)) {
                    setInboxes(data.inboxes)
                }
            } catch (error) {
                console.error('Error fetching inbox data:', error)
            }
        }

        fetchData()
    }, [])

    const [step, setStep] = useState(0) // 0 for file upload, 1 for additional details
    const [selectedOption, setSelectedOption] = useState('inputBoxes') // 'dropdown' or 'inputBoxes'

    const handleNext = () => {
        setStep(1)
    }

    const handleBack = () => {
        setStep(0)
    }

    const errorUpload = () => {
        toast.error('Oops, something went wrong, please try again')
        setLoading(false)
    }

    const handleUpload = async () => {
        setLoading(true)
        // Create the payload based on selected option
        let payload = {
            action: 'uploadFile',
            workspaceID: workspaceID,
            userID: userID,
            userName: userName,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
            workbookRange: newInboxRange,
        }

        if (selectedOption === 'dropdown') {
            payload.inboxName = selectedInbox
            payload.isNewInbox = false
        } else {
            payload.inboxName = newInboxName
            payload.inboxDescription = newInboxDescription
            if (newInboxRange.trim().length > 0) {
                payload.inboxRange = newInboxRange
            }
            payload.isNewInbox = true
        }
        trackEvent(EVENTS.DATASET.UPLOAD_ATTEMPT, {
            file: {
                name: safeS3FileName(acceptedFiles[0].name),
            },
        })
        postDataWorkspaceactions({
            ...payload,
            fileName: safeS3FileName(acceptedFiles[0].name),
            fileType: acceptedFiles[0].type,
        })
            .then((res) => {
                // Use the presigned URL to upload the file
                fetch(res.signedRequest, {
                    method: 'PUT',
                    body: acceptedFiles[0],
                    headers: {
                        'Content-Type': acceptedFiles[0].type,
                    },
                })
                    .then(() => {
                        toast.success('File uploaded successfully! Creating your new dataset...')
                        setCreationPolling(true)
                    })
                    .catch(() => {
                        errorUpload()
                    })
            })
            .catch(() => {
                errorUpload()
            })
    }

    const isUploadDisabled = useMemo(() => {
        if (selectedOption === 'dropdown') {
            return !selectedInbox
        } else {
            return newInboxName.length < 3
        }
    }, [selectedOption, selectedInbox, newInboxName])

    const dialogActions = (
        <ScoopRow className={styles.dialogActions}>
            {step === 0 && (
                <>
                    <ScoopButton onClick={stepBack} variant="grey">
                        Back
                    </ScoopButton>
                    <ScoopButton onClick={handleNext} disabled={acceptedFiles.length === 0}>
                        Next
                    </ScoopButton>
                </>
            )}
            {step === 1 && (
                <>
                    {!loading && (
                        <ScoopButton onClick={handleBack} variant="grey">
                            Back
                        </ScoopButton>
                    )}
                    <ScoopButton onClick={handleUpload} disabled={isUploadDisabled || loading}>
                        Upload
                    </ScoopButton>
                </>
            )}
        </ScoopRow>
    )

    const inboxOptions = inboxes.map((inbox) => ({
        label: inbox.label,
        value: inbox.inboxName,
    }))

    return (
        <>
            <ScoopDialog
                open={isOpen}
                onClose={onClose}
                title={'Dataset From Report'}
                actions={dialogActions}
                maxWidth="md"
            >
                {loading && (
                    <ScoopColumn className={styles.loadingContainer}>
                        <ScoopSpinner />
                        <ScoopText className={`inter ${styles.loadingTitle}`}>
                            {creationPolling ? 'Creating your dataset' : 'Uploading your file'}...
                        </ScoopText>
                        <ScoopText className={`inter ${styles.loadingSubtitle}`}>
                            This might take a few minutes depending on the size of your file
                        </ScoopText>
                    </ScoopColumn>
                )}
                {!loading && step === 0 && (
                    <section className={styles.section}>
                        <ScoopDropzone
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            isFocused={isFocused}
                            isDragAccept={isDragAccept}
                            isDragReject={isDragReject}
                            acceptedFiles={acceptedFiles}
                            icon={<UploadSimpleIcon size={48} weight="light" />}
                            hint="CSV, XLSX, HTML or other"
                            mainText="Drag 'n' drop your data file here or"
                            linkText="click here to select"
                        />
                        <ScoopText className={styles.noteText}>
                            Note: For best results in processing your data, ensure data format is
                            structured tabular.
                        </ScoopText>
                    </section>
                )}

                {!loading && step === 1 && (
                    <section className={styles.section}>
                        <div>
                            {acceptedFiles.map((file) => (
                                <ScoopRow className={styles.fileBox} key={file.path}>
                                    <FileIcon size={20} weight="regular" />
                                    <div className={styles.fileName}>{file.path}</div>
                                    <TrashIcon
                                        size={18}
                                        weight="regular"
                                        className={styles.trashIcon}
                                    />
                                </ScoopRow>
                            ))}
                        </div>
                        {!isOnboarding && (
                            <div className={styles.radioGroupContainer}>
                                <ScoopRadioButton
                                    name="uploadOption"
                                    value="inputBoxes"
                                    checked={selectedOption === 'inputBoxes'}
                                    onChange={() => setSelectedOption('inputBoxes')}
                                    label="Upload to a new dataset"
                                    size="sm"
                                />
                                <ScoopRadioButton
                                    name="uploadOption"
                                    value="dropdown"
                                    checked={selectedOption === 'dropdown'}
                                    onChange={() => setSelectedOption('dropdown')}
                                    label="Upload to an existing dataset"
                                    size="sm"
                                />
                            </div>
                        )}
                        {selectedOption === 'dropdown' && (
                            <div>
                                <ScoopSelector
                                    label={'Select inbox'}
                                    fullWidth
                                    value={selectedInbox}
                                    onChange={(e) => setSelectedInbox(e.target.value)}
                                    options={inboxOptions}
                                />
                            </div>
                        )}
                        {selectedOption === 'inputBoxes' && (
                            <div className={styles.formControl}>
                                <ScoopTextField
                                    padding={'8px'}
                                    placeholder={'My data file'}
                                    label="Name"
                                    value={newInboxName}
                                    onChange={(e) =>
                                        (e.target.value === '' ||
                                            /^[a-zA-Z0-9]+$/.test(e.target.value)) &&
                                        setNewInboxName(e.target.value)
                                    }
                                />
                                {acceptedFiles[0].path.endsWith('.xlsx') && (
                                    <ScoopTextField
                                        padding={'8px'}
                                        placeholder={'My named range'}
                                        label="Named Range(s) to Use (optional, comma separated)"
                                        value={newInboxRange}
                                        onChange={(e) => setNewInboxRange(e.target.value)}
                                    />
                                )}
                                <ScoopTextField
                                    multiline
                                    rows={2}
                                    label="Description (optional)"
                                    value={newInboxDescription}
                                    onChange={(e) => setNewInboxDescription(e.target.value)}
                                />
                            </div>
                        )}
                    </section>
                )}
            </ScoopDialog>
        </>
    )
}
